import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { EmployeeDocumentDto } from "@/core/features/employee-documents/employee-document.dto";
import { CreateEmployeeDocumentVersionDto } from "@/core/features/employee-documents/create-employee-document-version.dto";
import { ChangeEmployeeDocumentCategoryDto } from "@/core/features/employee-documents/change-employee-document-category.dto";
import { plainToInstance } from "class-transformer";
import { HttpRequest } from "@/core/common/http/http-request";
import { EmployeeDocumentVersionDto } from "@/core/features/employee-documents/employee-document-version.dto";
import { CreateEmployeeDocumentSignedUploadDto } from "@/core/features/employee-documents/create-employee-document-signed-upload.dto";
import { RegisterEmployeeDocumentSignedUploadDto } from "@/core/features/employee-documents/register-employee-document-signed-upload.dto";
import { ReviewEmployeeDocumentDto } from "@/core/features/employee-documents/review-employee-document.dto";

export class EmployeeDocumentsResource extends ResourceBaseService<
  EmployeeDocumentDto,
  CreateEmployeeDocumentVersionDto,
  ChangeEmployeeDocumentCategoryDto
> {
  protected EntityDto = EmployeeDocumentDto;
  protected CreateEntityDto = CreateEmployeeDocumentVersionDto;
  protected UpdateEntityDto = ChangeEmployeeDocumentCategoryDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/employee-documents");
  }

  async listByEmployee(employeeId: string): Promise<EmployeeDocumentDto[]> {
    const documents = await this.apiHttpClient.request<null, EmployeeDocumentDto[]>(
      HttpRequest.create({
        url: this.resourceUrl + "/employee/" + employeeId,
        auth: true,
      })
    );

    return plainToInstance(EmployeeDocumentDto, documents);
  }

  async listByEmployeeForReview(employeeId: string): Promise<EmployeeDocumentDto[]> {
    const documents = await this.apiHttpClient.request<null, EmployeeDocumentDto[]>(
      HttpRequest.create({
        url: this.resourceUrl + "/review/employee/" + employeeId,
        auth: true,
      })
    );

    return plainToInstance(EmployeeDocumentDto, documents);
  }

  async uploadDocumentVersion(file: File, dto: CreateEmployeeDocumentVersionDto): Promise<EmployeeDocumentVersionDto> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("employeeId", dto.employeeId);
    formData.append("categoryId", dto.categoryId);
    formData.append("origin", dto.origin);
    if (dto.filename) {
      formData.append("filename", dto.filename);
    }

    const documentVersion = await this.apiHttpClient.request<FormData, EmployeeDocumentVersionDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/upload",
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );

    return plainToInstance(EmployeeDocumentVersionDto, documentVersion);
  }

  async createSignedUpload(dto: CreateEmployeeDocumentSignedUploadDto): Promise<{ key: string; url: string }> {
    return this.apiHttpClient.request<CreateEmployeeDocumentSignedUploadDto, { key: string; url: string }>(
      HttpRequest.create({
        url: this.resourceUrl + "/signed-upload",
        method: "POST",
        auth: true,
        body: plainToInstance(CreateEmployeeDocumentSignedUploadDto, dto, { excludeExtraneousValues: true }),
      })
    );
  }

  async registerSignedUpload(dto: RegisterEmployeeDocumentSignedUploadDto): Promise<EmployeeDocumentVersionDto> {
    const documentVersion = await this.apiHttpClient.request<
      RegisterEmployeeDocumentSignedUploadDto,
      EmployeeDocumentVersionDto
    >(
      HttpRequest.create({
        url: this.resourceUrl + "/signed-upload/register",
        method: "POST",
        auth: true,
        body: plainToInstance(RegisterEmployeeDocumentSignedUploadDto, dto, { excludeExtraneousValues: true }),
      })
    );

    return plainToInstance(EmployeeDocumentVersionDto, documentVersion);
  }

  async changeCategory(documentId: string, dto: ChangeEmployeeDocumentCategoryDto): Promise<EmployeeDocumentDto> {
    const document = await this.apiHttpClient.request<ChangeEmployeeDocumentCategoryDto, EmployeeDocumentDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + documentId + "/category",
        method: "PATCH",
        auth: true,
        body: plainToInstance(ChangeEmployeeDocumentCategoryDto, dto, { excludeExtraneousValues: true }),
      })
    );

    return plainToInstance(EmployeeDocumentDto, document);
  }

  async reviewDocument(versionId: string, dto: ReviewEmployeeDocumentDto): Promise<EmployeeDocumentVersionDto> {
    const documentVersion = await this.apiHttpClient.request<ReviewEmployeeDocumentDto, EmployeeDocumentVersionDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/versions/" + versionId + "/review",
        method: "PATCH",
        auth: true,
        body: plainToInstance(ReviewEmployeeDocumentDto, dto, { excludeExtraneousValues: true }),
      })
    );

    return plainToInstance(EmployeeDocumentVersionDto, documentVersion);
  }

  async getDownloadUrl(versionId: string): Promise<string> {
    return this.apiHttpClient.request<null, string>(
      HttpRequest.create({
        url: this.resourceUrl + "/versions/" + versionId + "/download-url",
        auth: true,
      })
    );
  }
}
